import { Form, Formik } from 'formik';
import { useContext, useState } from 'react';
import { useHistory } from 'react-router';
import { OutpostDestination, OutpostDestinationTypeMetadata, OutpostDestinationUpdateInput } from '../../../../../../../typings/outpost';
import Button from '../../../common/base/Button';
import Container from '../../../common/base/Container';
import Divider from '../../../common/base/Divider';
import Link from '../../../common/base/Link';
import Loading from '../../../common/base/Loading';
import Text from '../../../common/base/Text';
import { StyledCard } from '../../../common/base/Card';
import { useDialog } from '../../../common/Dialog';
import EditorInput from '../../../common/Form/Fields/EditorInput';
import TextInput from '../../../common/Form/Fields/TextInput';
import { Div } from '../../../common/helpers/StyledUtils';
import { useToasts } from '../../../common/Toast';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { TeamPermission } from '../../../contexts/TeamPermissionContext';
import LINKS from '../../../../configs/links';
import { KeyValueEntry, KeyValueMapField, SetupLinkField, validateField } from '../Destinations/DestinationFields';
import { DestinationTopicsFields, getDestinationTopicsInitialValues } from '../utils/outpostForms';
interface ConfigFormValues {
  config: Record<string, any>;
  credentials: Record<string, any>;
}
interface FilterFormValues {
  filter: string;
}
export const OPERATOR_EVENT_TOPIC_OPTIONS = [{
  value: 'alert.destination.consecutive_failure',
  label: 'alert.destination.consecutive_failure'
}, {
  value: 'alert.destination.disabled',
  label: 'alert.destination.disabled'
}, {
  value: 'alert.attempt.exhausted_retries',
  label: 'alert.attempt.exhausted_retries'
}, {
  value: 'tenant.subscription.updated',
  label: 'tenant.subscription.updated'
},
// Emitted once per delivery attempt (Outpost v1.1+) — orders of magnitude
// more volume than the alert topics.
{
  value: 'attempt.success',
  label: 'attempt.success'
}, {
  value: 'attempt.failed',
  label: 'attempt.failed'
}];
const TopicsSection: React.FC<{
  destination: OutpostDestination;
  mutate: (data: OutpostDestination) => void;
}> = ({
  destination,
  mutate
}) => {
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    addToast
  } = useToasts();
  const initial_values = getDestinationTopicsInitialValues(destination.topics);
  return <>
      <Text as="h2" size="l" bold m={{
      t: 0,
      b: 0
    }}>
        Topics
      </Text>
      <Text as="p" muted m={{
      b: 4
    }}>
        Select which operator event types this destination receives.
      </Text>
      <Formik enableReinitialize initialValues={initial_values} onSubmit={async values => {
      try {
        const topics = values.all_topics ? ['*'] : values.topics;
        const updated = await HookdeckAPI.outpost.operatorEvents.destinations.update(destination.id, {
          topics
        });
        mutate(updated);
        addToast('success', 'Topics updated');
      } catch {
        addToast('error', 'Failed to update topics');
      }
    }}>
        {props => <Form>
            <DestinationTopicsFields topic_options={OPERATOR_EVENT_TOPIC_OPTIONS} form_values={props.values} />
            <Div flex={{
          align: 'center',
          gap: 4
        }}>
              <Button.Permission primary disabled={!props.dirty || props.isSubmitting} submit icon={props.isSubmitting ? 'loading' : 'save'}>
                Save
              </Button.Permission>
              {props.dirty && <Text muted size="m">
                  You have unsaved changes
                </Text>}
            </Div>
          </Form>}
      </Formik>
    </>;
};
const OperatorEventDestinationSettings: React.FC<{
  destination: OutpostDestination;
  destination_type?: OutpostDestinationTypeMetadata;
  mutate: (data: OutpostDestination) => void;
}> = ({
  destination,
  destination_type,
  mutate
}) => {
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    addToast
  } = useToasts();
  const show_dialog = useDialog();
  const history = useHistory();
  const [is_submitting_config, setIsSubmittingConfig] = useState(false);
  const [editing_fields, setEditingFields] = useState<Record<string, boolean>>({});
  const [is_filter_enabled, setIsFilterEnabled] = useState(!!(destination.filter && Object.keys(destination.filter).length > 0));
  const [is_submitting_filter, setIsSubmittingFilter] = useState(false);
  const [is_rotating_secret, setIsRotatingSecret] = useState(false);
  const config_initial_values: ConfigFormValues = {
    config: destination_type ? destination_type.config_fields.reduce((acc, field) => {
      if (field.type === 'key_value_map') {
        const existing = destination.config[field.key];
        acc[field.key] = existing && typeof existing === 'object' ? Object.entries(existing).length > 0 ? Object.entries(existing) : [['', '']] : [['', '']];
      } else {
        acc[field.key] = destination.config[field.key] ?? field.default ?? '';
      }
      return acc;
    }, {} as Record<string, any>) : {},
    credentials: destination_type ? destination_type.credential_fields.reduce((acc_0, field_0) => {
      acc_0[field_0.key] = destination.credentials?.[field_0.key] ?? '';
      return acc_0;
    }, {} as Record<string, any>) : {}
  };
  const filter_initial_values: FilterFormValues = (() => {
    const raw = destination.filter;
    const has_raw = raw && typeof raw === 'object' && Object.keys(raw).length > 0;
    const obj: Record<string, any> = has_raw ? {
      ...raw
    } : {
      data: {},
      metadata: {}
    };
    return {
      filter: JSON.stringify(obj, null, 2)
    };
  })();
  const onSubmitConfig = async (values: ConfigFormValues) => {
    setIsSubmittingConfig(true);
    try {
      const serialized_config: Record<string, any> = {};
      if (destination_type) {
        for (const field_1 of destination_type.config_fields) {
          if (field_1.type === 'key_value_map') {
            const entries = values.config[field_1.key];
            if (Array.isArray(entries)) {
              const map: Record<string, string> = {};
              for (const [k, v] of entries as KeyValueEntry[]) {
                if (k.trim()) {
                  map[k.trim()] = v;
                }
              }
              serialized_config[field_1.key] = map;
            }
          } else if (field_1.sensitive && !editing_fields[`config.${field_1.key}`]) {
            // Skip sensitive fields that haven't been edited
          } else {
            serialized_config[field_1.key] = values.config[field_1.key];
          }
        }
      }
      const update_data: OutpostDestinationUpdateInput = {
        config: serialized_config
      };
      const filled_credentials = Object.entries(values.credentials).reduce((acc_1, [key, value]) => {
        if (editing_fields[`credentials.${key}`] && value && String(value).trim()) {
          acc_1[key] = value;
        }
        return acc_1;
      }, {} as Record<string, any>);
      if (Object.keys(filled_credentials).length > 0) {
        update_data.credentials = filled_credentials;
      }
      const updated = await HookdeckAPI.outpost.operatorEvents.destinations.update(destination.id, update_data);
      mutate(updated);
      setEditingFields({});
      addToast('success', 'Configuration updated');
    } catch (e: any) {
      addToast('error', `An error occurred while updating the configuration${e.response?.message ? `: ${e.response.message}` : ''}`);
    } finally {
      setIsSubmittingConfig(false);
    }
  };
  const onSubmitFilter = async (values_0: FilterFormValues) => {
    setIsSubmittingFilter(true);
    try {
      let filter_data: Record<string, any> = {};
      try {
        filter_data = JSON.parse(values_0.filter);
      } catch {
        addToast('error', 'Invalid JSON in filter');
        setIsSubmittingFilter(false);
        return;
      }
      const is_non_empty_object = (v_0: unknown) => v_0 != null && typeof v_0 === 'object' && !Array.isArray(v_0) && Object.keys(v_0 as Record<string, unknown>).length > 0;
      const trimmed: Record<string, any> = {};
      if (is_non_empty_object(filter_data.data)) trimmed.data = filter_data.data;
      if (is_non_empty_object(filter_data.metadata)) trimmed.metadata = filter_data.metadata;
      const has_filter = Object.keys(trimmed).length > 0;
      const updated_0 = await HookdeckAPI.outpost.operatorEvents.destinations.update(destination.id, {
        filter: has_filter ? trimmed : {}
      });
      mutate(updated_0);
      addToast('success', 'Filter updated');
    } catch (e_0: any) {
      addToast('error', `An error occurred while updating the filter${e_0.response?.message ? `: ${e_0.response.message}` : ''}`);
    } finally {
      setIsSubmittingFilter(false);
    }
  };
  const onRemoveFilter = async () => {
    setIsSubmittingFilter(true);
    try {
      const updated_1 = await HookdeckAPI.outpost.operatorEvents.destinations.update(destination.id, {
        filter: {}
      });
      mutate(updated_1);
      setIsFilterEnabled(false);
      addToast('success', 'Filter removed');
    } catch (e_1: any) {
      addToast('error', `An error occurred while removing the filter${e_1.response?.message ? `: ${e_1.response.message}` : ''}`);
    } finally {
      setIsSubmittingFilter(false);
    }
  };
  const onRotateSecret = () => {
    show_dialog(async () => {
      setIsRotatingSecret(true);
      try {
        const updated_2 = await HookdeckAPI.outpost.operatorEvents.destinations.update(destination.id, {
          credentials: {
            rotate_secret: true
          }
        });
        mutate(updated_2);
        addToast('success', 'Secret rotated successfully. The previous secret will be invalidated after 24 hours.');
      } catch (e_2: any) {
        addToast('error', `An error occurred while rotating the secret${e_2.response?.message ? `: ${e_2.response.message}` : ''}`);
      } finally {
        setIsRotatingSecret(false);
      }
    }, () => null, {
      title: 'Rotate secret',
      submit_label: 'Rotate',
      cancel_label: 'Cancel',
      message: 'This will generate a new signing secret. The current secret will remain valid for 24 hours. Are you sure?'
    });
  };
  const onToggleEnabled = async () => {
    try {
      const updated_3 = destination.disabled_at ? await HookdeckAPI.outpost.operatorEvents.destinations.enable(destination.id) : await HookdeckAPI.outpost.operatorEvents.destinations.disable(destination.id);
      mutate(updated_3);
      addToast('success', destination.disabled_at ? 'Destination enabled' : 'Destination disabled');
    } catch {
      addToast('error', 'Failed to update destination status');
    }
  };
  const onDelete = () => {
    show_dialog(() => {
      HookdeckAPI.outpost.operatorEvents.destinations.delete(destination.id).then(() => {
        addToast('success', 'Operator event destination deleted');
        history.push('/settings/project/monitoring');
      }).catch(() => {
        addToast('error', 'Failed to delete operator event destination');
      });
    }, () => null, {
      danger: true,
      title: 'Delete operator event destination',
      submit_label: 'Delete',
      cancel_label: 'Keep destination',
      message: <Text>
            Are you sure you want to delete this operator event destination? You will no longer
            receive notifications at this destination.
          </Text>
    });
  };
  return <Div>
      <Container medium left>
        <TeamPermission role="member">
          <Text as="h2" size="l" bold m={{
          t: 8,
          b: 0
        }}>
            Configuration
          </Text>
          <Text as="p" muted m={{
          b: 4
        }}>
            Update configuration and credentials for this destination.
          </Text>
          {destination_type ? <Formik<ConfigFormValues> initialValues={config_initial_values} enableReinitialize onSubmit={onSubmitConfig}>
              {({
            dirty,
            setFieldValue
          }) => <Form>
                  {destination_type.config_fields.filter(f_0 => f_0.type !== 'key_value_map').length > 0 && destination_type.config_fields.filter(f => f.type !== 'key_value_map').map(field_2 => {
              if (field_2.sensitive) {
                const edit_key = `config.${field_2.key}`;
                const is_editing = editing_fields[edit_key];
                return <Div key={field_2.key} flex={{
                  align: 'flex-end',
                  gap: 2
                }} m={{
                  b: 2
                }}>
                              <Div flex={{
                    grow: true
                  }}>
                                <TextInput name={edit_key} label={field_2.label} help={field_2.description} required={is_editing ? field_2.required : false} type={is_editing ? 'password' : 'text'} disabled={!is_editing} validate={is_editing && field_2.pattern ? validateField(field_2) : undefined} m={0} mono />
                              </Div>
                              <Button outline icon={is_editing ? 'close' : 'edit'} onClick={() => {
                    if (is_editing) {
                      setFieldValue(edit_key, destination.config[field_2.key] ?? field_2.default ?? '');
                      setEditingFields(prev => ({
                        ...prev,
                        [edit_key]: false
                      }));
                    } else {
                      setFieldValue(edit_key, '');
                      setEditingFields(prev_0 => ({
                        ...prev_0,
                        [edit_key]: true
                      }));
                    }
                  }} />
                            </Div>;
              }
              return <TextInput key={field_2.key} name={`config.${field_2.key}`} label={field_2.label} help={field_2.description} required={field_2.required} type="text" validate={field_2.pattern ? validateField(field_2) : undefined} mono />;
            })}
                  {destination_type.config_fields.filter(f_1 => f_1.type === 'key_value_map').map(field_3 => <KeyValueMapField key={field_3.key} field_key={field_3.key} label={field_3.label} />)}
                  {destination_type.credential_fields.length > 0 && <SetupLinkField destination_type={destination_type} />}
                  {destination_type.credential_fields.length > 0 && destination_type.credential_fields.map(field_4 => {
              const edit_key_0 = `credentials.${field_4.key}`;
              const is_editing_0 = editing_fields[edit_key_0];
              return <Div key={field_4.key} flex={{
                align: 'center',
                gap: 2
              }} m={{
                b: 2
              }}>
                          <TextInput name={edit_key_0} w={100} label={field_4.label} help={field_4.description} placeholder={is_editing_0 ? 'Enter new value' : ''} type={is_editing_0 ? 'password' : 'text'} disabled={!is_editing_0} validate={is_editing_0 && field_4.pattern ? validateField(field_4) : undefined} m={0} mono />
                          <Button outline large icon={is_editing_0 ? 'close' : 'edit'} onClick={() => {
                  if (is_editing_0) {
                    setFieldValue(edit_key_0, destination.credentials?.[field_4.key] ?? '');
                    setEditingFields(prev_1 => ({
                      ...prev_1,
                      [edit_key_0]: false
                    }));
                  } else {
                    setFieldValue(edit_key_0, '');
                    setEditingFields(prev_2 => ({
                      ...prev_2,
                      [edit_key_0]: true
                    }));
                  }
                }} />
                        </Div>;
            })}
                  <Div m={{
              t: 4
            }} flex={{
              gap: 4,
              align: 'center'
            }}>
                    <Button.Permission primary disabled={is_submitting_config || !dirty} submit icon={is_submitting_config ? 'loading' : 'save'}>
                      Save
                    </Button.Permission>
                    {dirty && <Text muted size="m">
                        You have unsaved changes
                      </Text>}
                  </Div>
                </Form>}
            </Formik> : <Loading />}
        </TeamPermission>
      </Container>
      <Div m={{
      x: 6,
      t: 10,
      b: 8
    }}>
        <Divider />
      </Div>
      <Container medium left>
        <TeamPermission role="member">
          <TopicsSection destination={destination} mutate={mutate} />
        </TeamPermission>
      </Container>
      <Div m={{
      x: 6,
      t: 10,
      b: 8
    }}>
        <Divider />
      </Div>
      <Container medium left>
        <TeamPermission role="member">
          <Text as="h2" size="l" bold m={0}>
            Event filter
          </Text>
          <Text as="p" muted m={{
          b: 4
        }}>
            Filter events based on their data or metadata. Only events matching the filter will be
            delivered.{' '}
            <Link href={LINKS.outpost.filters} target="_blank" icon="link">
              View documentation
            </Link>
          </Text>
          {is_filter_enabled ? <Formik<FilterFormValues> initialValues={filter_initial_values} enableReinitialize validate={values_1 => {
          const errors: Partial<Record<keyof FilterFormValues, string>> = {};
          const raw_0 = values_1.filter?.trim() ?? '';
          if (raw_0) {
            try {
              const parsed = JSON.parse(raw_0);
              if (typeof parsed !== 'object' || Array.isArray(parsed)) {
                errors.filter = 'Filter must be a JSON object with "data" and/or "metadata" keys';
              } else {
                const top_level = Object.keys(parsed);
                const invalid = top_level.filter(k_0 => k_0 !== 'data' && k_0 !== 'metadata');
                if (invalid.length > 0) {
                  errors.filter = 'Filter may only contain top-level "data" and "metadata" keys';
                } else if (!top_level.includes('data') && !top_level.includes('metadata')) {
                  errors.filter = 'Filter must include at least one of "data" or "metadata" at the top level';
                } else {
                  const data_empty = !parsed.data || typeof parsed.data !== 'object' || Array.isArray(parsed.data) || Object.keys(parsed.data).length === 0;
                  const metadata_empty = !parsed.metadata || typeof parsed.metadata !== 'object' || Array.isArray(parsed.metadata) || Object.keys(parsed.metadata).length === 0;
                  if (data_empty && metadata_empty) {
                    errors.filter = 'At least one of "data" or "metadata" must be a non-empty object';
                  }
                }
              }
            } catch {
              errors.filter = 'Invalid JSON';
            }
          }
          return errors;
        }} onSubmit={onSubmitFilter}>
              {({
            dirty: dirty_0,
            errors: errors_0
          }) => <Form>
                  <StyledCard overflow_hidden m={{
              b: 4
            }}>
                    <EditorInput name="filter" language="json" format_on_blur display="minimal" height="200px" />
                  </StyledCard>
                  <Div flex={{
              gap: 4,
              align: 'center'
            }}>
                    <Button.Permission primary disabled={is_submitting_filter || !dirty_0 || !!errors_0.filter} submit icon={is_submitting_filter ? 'loading' : 'save'}>
                      Save
                    </Button.Permission>
                    <Button.Permission outline disabled={is_submitting_filter} onClick={onRemoveFilter} icon={is_submitting_filter ? 'loading' : 'close'}>
                      Remove filter
                    </Button.Permission>
                    {dirty_0 && <Text muted size="m">
                        You have unsaved changes
                      </Text>}
                  </Div>
                </Form>}
            </Formik> : <Button.Permission outline onClick={() => setIsFilterEnabled(true)} icon="filter_list">
              Add filter
            </Button.Permission>}
        </TeamPermission>
      </Container>
      {destination.type === 'webhook' && <>
          <Div m={{
        x: 6,
        t: 10,
        b: 8
      }}>
            <Divider />
          </Div>
          <Container medium left>
            <TeamPermission role="member">
              <Text as="h2" size="l" bold m={0}>
                Rotate Secret
              </Text>
              <Text as="p" muted m={{
            b: 4
          }}>
                Rotate the destination signing secret. This will generate a new secret and
                invalidate the current one after 24 hours.
              </Text>
              <Button.Permission outline onClick={onRotateSecret} icon={is_rotating_secret ? 'loading' : 'reload'} disabled={is_rotating_secret}>
                Rotate
              </Button.Permission>
            </TeamPermission>
          </Container>
        </>}
      <Div m={{
      x: 6,
      t: 10,
      b: 8
    }}>
        <Divider />
      </Div>
      <Container medium left>
        <TeamPermission role="member">
          <Text as="h2" size="l" bold m={0}>
            {destination.disabled_at ? 'Enable destination' : 'Disable destination'}
          </Text>
          <Text as="p" muted m={{
          b: 4
        }}>
            {destination.disabled_at ? 'Re-enable this destination to resume operator event deliveries.' : 'Disable this destination to temporarily stop operator event deliveries. You can re-enable it later.'}
          </Text>
          {destination.disabled_at ? <Button.Permission primary outline onClick={onToggleEnabled} icon="enable">
              Enable destination
            </Button.Permission> : <Button.Permission danger outline onClick={onToggleEnabled} icon="disable">
              Disable destination
            </Button.Permission>}
        </TeamPermission>
      </Container>
      <Div m={{
      x: 6,
      t: 10,
      b: 8
    }}>
        <Divider />
      </Div>
      <Container medium left m={{
      b: 20
    }}>
        <TeamPermission role="member">
          <Text as="h2" size="l" bold m={0}>
            Delete destination
          </Text>
          <Text as="p" muted m={{
          b: 4
        }}>
            Permanently delete this operator event destination. You will no longer receive operator
            event notifications at this destination.
          </Text>
          <Button.Permission danger outline onClick={onDelete} icon="delete">
            Delete destination
          </Button.Permission>
        </TeamPermission>
      </Container>
    </Div>;
};
export default OperatorEventDestinationSettings;